import { supabase } from './client';
import type {
  AdHookRow,
  AdHookInsert,
  AdHookUpdate,
  AdContentRow,
  AdContentInsert,
  AdContentUpdate,
  AdCtaRow,
  AdCtaInsert,
  AdCtaUpdate,
  AdCombinationRow,
  AdCombinationInsert,
  AdCombinationUpdate,
  AdHookCategory,
  AdContentType,
  AdCtaType,
  AdElementStatus,
} from './database.types';

// ============================================
// Frontend Data Interfaces (camelCase)
// ============================================

export interface AdHookData {
  id: string;
  createdAt: string;
  updatedAt: string;
  text: string;
  category: AdHookCategory;
  score: number;
  timesUsed: number;
  avgCtr: number;
  avgCpl: number;
  avgHookRate: number;
  status: AdElementStatus;
  notes: string | null;
  videoUrl: string | null;
}

export interface AdContentData {
  id: string;
  createdAt: string;
  updatedAt: string;
  text: string;
  contentType: AdContentType;
  score: number;
  timesUsed: number;
  avgCtr: number;
  avgCpl: number;
  status: AdElementStatus;
  notes: string | null;
  videoUrl: string | null;
}

export interface AdCtaData {
  id: string;
  createdAt: string;
  updatedAt: string;
  text: string;
  ctaType: AdCtaType;
  score: number;
  timesUsed: number;
  avgCtr: number;
  avgCpl: number;
  status: AdElementStatus;
  notes: string | null;
  videoUrl: string | null;
}

export interface AdCombinationMetrics {
  spend: number;
  impressions: number;
  clicks: number;
  leads: number;
  ctr: number;
  cpl: number;
  hookRate: number;
}

export interface AdCombinationData {
  id: string;
  createdAt: string;
  updatedAt: string;
  hookId: string;
  contentId: string;
  ctaId: string;
  campaignId: string;
  campaignName: string;
  metrics: AdCombinationMetrics | null;
  lastSyncedAt: string | null;
}

// ============================================
// Mapping Helpers
// ============================================

const mapHookRow = (row: AdHookRow): AdHookData => ({
  id: row.id,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  text: row.text,
  category: row.category,
  score: row.score,
  timesUsed: row.times_used,
  avgCtr: row.avg_ctr,
  avgCpl: row.avg_cpl,
  avgHookRate: row.avg_hook_rate,
  status: row.status,
  notes: row.notes,
  videoUrl: row.video_url,
});

const mapContentRow = (row: AdContentRow): AdContentData => ({
  id: row.id,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  text: row.text,
  contentType: row.content_type,
  score: row.score,
  timesUsed: row.times_used,
  avgCtr: row.avg_ctr,
  avgCpl: row.avg_cpl,
  status: row.status,
  notes: row.notes,
  videoUrl: row.video_url,
});

const mapCtaRow = (row: AdCtaRow): AdCtaData => ({
  id: row.id,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  text: row.text,
  ctaType: row.cta_type,
  score: row.score,
  timesUsed: row.times_used,
  avgCtr: row.avg_ctr,
  avgCpl: row.avg_cpl,
  status: row.status,
  notes: row.notes,
  videoUrl: row.video_url,
});

const mapCombinationRow = (row: AdCombinationRow): AdCombinationData => ({
  id: row.id,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  hookId: row.hook_id,
  contentId: row.content_id,
  ctaId: row.cta_id,
  campaignId: row.campaign_id,
  campaignName: row.campaign_name,
  metrics: row.metrics_json as AdCombinationMetrics | null,
  lastSyncedAt: row.last_synced_at,
});

// ============================================
// Ad Hooks API
// ============================================

export const adHooksApi = {
  async getAll(): Promise<AdHookData[]> {
    const { data, error } = await supabase
      .from('ad_hooks')
      .select('*')
      .order('score', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapHookRow);
  },

  async create(hook: Omit<AdHookInsert, 'id' | 'created_at' | 'updated_at'>): Promise<AdHookData> {
    const { data, error } = await supabase
      .from('ad_hooks')
      .insert(hook)
      .select()
      .single();

    if (error) throw error;
    return mapHookRow(data);
  },

  async update(id: string, updates: AdHookUpdate): Promise<AdHookData> {
    const { data, error } = await supabase
      .from('ad_hooks')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return mapHookRow(data);
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('ad_hooks')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ============================================
// Ad Content API
// ============================================

export const adContentApi = {
  async getAll(): Promise<AdContentData[]> {
    const { data, error } = await supabase
      .from('ad_content')
      .select('*')
      .order('score', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapContentRow);
  },

  async create(content: Omit<AdContentInsert, 'id' | 'created_at' | 'updated_at'>): Promise<AdContentData> {
    const { data, error } = await supabase
      .from('ad_content')
      .insert(content)
      .select()
      .single();

    if (error) throw error;
    return mapContentRow(data);
  },

  async update(id: string, updates: AdContentUpdate): Promise<AdContentData> {
    const { data, error } = await supabase
      .from('ad_content')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return mapContentRow(data);
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('ad_content')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ============================================
// Ad CTAs API
// ============================================

export const adCtasApi = {
  async getAll(): Promise<AdCtaData[]> {
    const { data, error } = await supabase
      .from('ad_ctas')
      .select('*')
      .order('score', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapCtaRow);
  },

  async create(cta: Omit<AdCtaInsert, 'id' | 'created_at' | 'updated_at'>): Promise<AdCtaData> {
    const { data, error } = await supabase
      .from('ad_ctas')
      .insert(cta)
      .select()
      .single();

    if (error) throw error;
    return mapCtaRow(data);
  },

  async update(id: string, updates: AdCtaUpdate): Promise<AdCtaData> {
    const { data, error } = await supabase
      .from('ad_ctas')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return mapCtaRow(data);
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('ad_ctas')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ============================================
// Ad Combinations API
// ============================================

export const adCombinationsApi = {
  async getAll(): Promise<AdCombinationData[]> {
    const { data, error } = await supabase
      .from('ad_combinations')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapCombinationRow);
  },

  async create(combo: Omit<AdCombinationInsert, 'id' | 'created_at' | 'updated_at'>): Promise<AdCombinationData> {
    const { data, error } = await supabase
      .from('ad_combinations')
      .insert(combo)
      .select()
      .single();

    if (error) throw error;
    return mapCombinationRow(data);
  },

  async update(id: string, updates: AdCombinationUpdate): Promise<AdCombinationData> {
    const { data, error } = await supabase
      .from('ad_combinations')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return mapCombinationRow(data);
  },

  async updateMetrics(id: string, metrics: AdCombinationMetrics): Promise<AdCombinationData> {
    const { data, error } = await supabase
      .from('ad_combinations')
      .update({
        metrics_json: metrics as any,
        last_synced_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return mapCombinationRow(data);
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('ad_combinations')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ============================================
// Score Calculation
// ============================================

/**
 * Calculate a score (0-100) from ad metrics.
 * Weighted: CPL (40%), CTR (30%), leads (20%), hook rate (10%)
 *
 * Benchmarks (exterior lighting industry):
 * - Good CPL: $15-25, Bad: $50+
 * - Good CTR: 2-4%, Bad: <1%
 * - Good hook rate: 30%+, Bad: <15%
 */
export const calculateElementScore = (metrics: {
  avgCpl: number;
  avgCtr: number;
  totalLeads: number;
  avgHookRate?: number;
}): number => {
  // CPL score (lower is better) - 40% weight
  let cplScore = 0;
  if (metrics.avgCpl <= 0) cplScore = 0; // no data
  else if (metrics.avgCpl <= 10) cplScore = 100;
  else if (metrics.avgCpl <= 20) cplScore = 85;
  else if (metrics.avgCpl <= 30) cplScore = 70;
  else if (metrics.avgCpl <= 40) cplScore = 55;
  else if (metrics.avgCpl <= 50) cplScore = 40;
  else cplScore = Math.max(10, 100 - metrics.avgCpl);

  // CTR score (higher is better) - 30% weight
  let ctrScore = 0;
  if (metrics.avgCtr >= 4) ctrScore = 100;
  else if (metrics.avgCtr >= 3) ctrScore = 85;
  else if (metrics.avgCtr >= 2) ctrScore = 70;
  else if (metrics.avgCtr >= 1) ctrScore = 50;
  else if (metrics.avgCtr > 0) ctrScore = 25;

  // Leads score (more is better) - 20% weight
  let leadsScore = 0;
  if (metrics.totalLeads >= 20) leadsScore = 100;
  else if (metrics.totalLeads >= 10) leadsScore = 80;
  else if (metrics.totalLeads >= 5) leadsScore = 60;
  else if (metrics.totalLeads >= 1) leadsScore = 40;

  // Hook rate score - 10% weight
  let hookScore = 50; // default if no data
  if (metrics.avgHookRate !== undefined && metrics.avgHookRate > 0) {
    if (metrics.avgHookRate >= 40) hookScore = 100;
    else if (metrics.avgHookRate >= 30) hookScore = 85;
    else if (metrics.avgHookRate >= 20) hookScore = 65;
    else if (metrics.avgHookRate >= 10) hookScore = 40;
    else hookScore = 20;
  }

  const score = Math.round(
    cplScore * 0.4 + ctrScore * 0.3 + leadsScore * 0.2 + hookScore * 0.1
  );

  return Math.min(100, Math.max(0, score));
};
